import { BadgeOutlined, CheckCircle, PersonOutline, Search } from "@mui/icons-material";
import { Alert, Box, Button, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import { useState } from "react";
import PropTypes from "prop-types";
import { getCustomerDisplayName } from "../utils";

const getApplicationLabel = (candidate) => candidate.appUsuario || candidate.tipoApp || "Sin aplicación";

const getCandidateName = (candidate) => {
  const name = `${candidate.nombre || ""} ${candidate.apellido || ""}`.trim();
  return name || candidate.username || `Usuario ${candidate.id}`;
};

export default function CustomerLookup({
  customer,
  candidates = [],
  loading = false,
  externalError = "",
  sampleCustomers,
  showHeader = true,
  onLookup,
  onSelect,
  onReset,
  onAnonymous,
}) {
  const [dni, setDni] = useState("");
  const [error, setError] = useState("");
  const normalizedInputDni = dni.replace(/\D/g, "");
  const candidatesMatchInput =
    candidates.length > 1 &&
    candidates.every((candidate) => String(candidate?.dni || "").replace(/\D/g, "") === normalizedInputDni);

  // Envío implícito del formulario: cubre Enter, el Enter del teclado numérico,
  // el sufijo que mandan los lectores de DNI y el "Ir" de un teclado móvil,
  // sin depender de event.key.
  const submitLookup = (event) => {
    event?.preventDefault?.();
    const normalizedDni = dni.replace(/\D/g, "");
    if (normalizedDni.length < 7 || normalizedDni.length > 9) {
      setError("Ingresá un DNI de 7 a 9 dígitos.");
      return;
    }
    setError("");
    onLookup(normalizedDni);
  };

  const loadSample = (sampleDni) => {
    setDni(sampleDni);
    setError("");
    onLookup(sampleDni);
  };

  return (
    <Box data-testid="customer-section">
      {showHeader && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#2C3087", letterSpacing: ".08em" }}>
              2 · CLIENTE
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#64748B" }}>Identificación y condición fiscal</Typography>
          </Box>
          {customer && <CheckCircle sx={{ color: "#2C9A83" }} />}
        </Box>
      )}

      {!customer ? (
        <>
          <Box component="form" onSubmit={submitLookup} noValidate sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              autoFocus
              value={dni}
              onChange={(event) => setDni(event.target.value.replace(/\D/g, "").slice(0, 9))}
              placeholder="Ingresar DNI"
              error={Boolean(error || externalError)}
              disabled={loading}
              inputProps={{ inputMode: "numeric", enterKeyHint: "search", "aria-label": "DNI del cliente" }}
            />
            <Button
              type="submit"
              variant="contained"
              aria-label="Consultar DNI"
              disabled={loading}
              sx={{
                bgcolor: "#2C3087",
                color: "#fff",
                minWidth: 44,
                px: 1.25,
                boxShadow: "none",
                "&:hover": { bgcolor: "#22266F" },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <Search />}
            </Button>
          </Box>
          {(error || externalError) && (
            <Typography role="alert" sx={{ color: "#D32F2F", fontSize: 12, mt: 0.75 }}>
              {error || externalError}
            </Typography>
          )}
          {candidatesMatchInput && (
            <Box
              data-testid="customer-candidates"
              sx={{ mt: 1.25, p: 1.25, border: "1px solid #DAD9F4", borderRadius: "12px", bgcolor: "#F8F7FF" }}
            >
              <Typography sx={{ color: "#172033", fontSize: 13, fontWeight: 900 }}>
                Elegí la cuenta correspondiente
              </Typography>
              <Typography sx={{ color: "#64748B", fontSize: 11.5, mt: 0.2, mb: 1 }}>
                Este DNI está registrado en más de una aplicación.
              </Typography>
              <Box sx={{ display: "grid", gap: 0.75 }}>
                {candidates.map((candidate) => {
                  const application = getApplicationLabel(candidate);
                  return (
                    <Button
                      key={`${application}-${candidate.id}-${candidate.username || candidate.email || "usuario"}`}
                      variant="outlined"
                      disabled={loading}
                      onClick={() => onSelect(candidate)}
                      data-testid={`customer-candidate-${application}-${candidate.id}`}
                      sx={{
                        justifyContent: "space-between",
                        gap: 1,
                        px: 1.25,
                        py: 0.85,
                        borderColor: "#CBD5E1",
                        bgcolor: "#fff",
                        color: "#172033",
                        textTransform: "none",
                        textAlign: "left",
                        "&:hover": { borderColor: "#2C3087", bgcolor: "#fff" },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 900, lineHeight: 1.2 }}>
                          {getCandidateName(candidate)}
                        </Typography>
                        <Typography sx={{ color: "#64748B", fontSize: 11.5, lineHeight: 1.3, mt: 0.2 }}>
                          DNI {candidate.dni}{candidate.tipoUser ? ` · ${candidate.tipoUser}` : ""}
                        </Typography>
                      </Box>
                      <Chip
                        label={application}
                        size="small"
                        sx={{ bgcolor: "#EEECFF", color: "#2C3087", fontSize: 10.5, fontWeight: 900, flexShrink: 0 }}
                      />
                    </Button>
                  );
                })}
              </Box>
            </Box>
          )}
          <Button
            onClick={onAnonymous}
            disabled={loading}
            startIcon={<PersonOutline />}
            sx={{ mt: 0.75, px: 0, textTransform: "none", color: "#475569", fontWeight: 700 }}
          >
            Continuar sin DNI
          </Button>
          <Box
            sx={{
              display: sampleCustomers.length ? "flex" : "none",
              flexWrap: "wrap",
              gap: 0.75,
              mt: 1,
            }}
          >
            <Typography sx={{ width: "100%", fontSize: 11, color: "#94A3B8" }}>DNI de prueba:</Typography>
            {sampleCustomers.map((item) => (
              <Chip
                key={item.dni}
                size="small"
                variant="outlined"
                label={`${item.dni} · ${item.communityRole}`}
                onClick={() => loadSample(item.dni)}
                disabled={loading}
                sx={{ fontSize: 11, fontWeight: 700 }}
              />
            ))}
            <Chip
              size="small"
              variant="outlined"
              label="28765432 · Particular"
              onClick={() => loadSample("28765432")}
              disabled={loading}
              sx={{ fontSize: 11, fontWeight: 700 }}
            />
          </Box>
        </>
      ) : (
        <>
          <Alert
            icon={customer.isCommunityMember ? <BadgeOutlined /> : <PersonOutline />}
            severity={customer.isCommunityMember ? "success" : "info"}
            action={
              <Button size="small" onClick={onReset} disabled={loading} sx={{ fontWeight: 800 }}>
                {loading ? "Actualizando…" : "Cambiar"}
              </Button>
            }
            sx={{ alignItems: "center", py: 0.5, "& .MuiAlert-message": { width: "100%" } }}
          >
            <Typography data-testid="customer-name" sx={{ fontWeight: 800, fontSize: 14 }}>
              {getCustomerDisplayName(customer)}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              {customer.isCommunityMember
                ? `${customer.communityRole} UPrO · Tratamiento sin IVA`
                : `${customer.dni ? `DNI ${customer.dni} · ` : ""}Consumidor final · IVA discriminado`}
            </Typography>
          </Alert>
          {externalError && (
            <Typography sx={{ color: "#D32F2F", fontSize: 12, mt: 0.75 }}>{externalError}</Typography>
          )}
        </>
      )}
    </Box>
  );
}

CustomerLookup.propTypes = {
  customer: PropTypes.object,
  candidates: PropTypes.arrayOf(PropTypes.object).isRequired,
  sampleCustomers: PropTypes.arrayOf(PropTypes.object).isRequired,
  showHeader: PropTypes.bool,
  loading: PropTypes.bool,
  externalError: PropTypes.string,
  onLookup: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onAnonymous: PropTypes.func.isRequired,
};
